import React from 'react';
import Icon from '../../../components/AppIcon';

import { formatCurrency } from '../../../utils/currency';

const DebtCard = ({ debt, onEdit, onDelete, onViewHistory, onViewPlans }) => {
  const totalMonthlyPayment = debt?.minimumPayment + debt?.additionalFees + debt?.insuranceFee;
  const progressPercentage = ((debt?.principalAmount - debt?.currentBalance) / debt?.principalAmount) * 100;
  const monthlyInterest = (debt?.currentBalance * (debt?.interestRate / 100)) / 12;

  const getStrategyIcon = (strategy) => {
    const iconMap = {
      'avalanche': 'TrendingDown',
      'snowball': 'Target',
      'hybrid': 'Zap',
      'custom': 'Settings'
    };
    return iconMap?.[strategy] || 'FileText';
  };

  const getStrategyColor = (strategy) => {
    const colorMap = {
      'avalanche': 'var(--color-primary)',
      'snowball': 'var(--color-success)',
      'hybrid': 'var(--color-warning)',
      'custom': 'var(--color-muted-foreground)'
    };
    return colorMap?.[strategy] || 'var(--color-muted-foreground)';
  };

  const isPaidOff = debt?.status === 'paid_off';

  return (
    <div className={`bg-card rounded-lg border border-border p-20 md:p-24 shadow-elevation-2 hover:shadow-elevation-3 transition-smooth ${isPaidOff ? 'opacity-70' : ''}`}>
      <div className="flex items-start justify-between mb-16">
        <div className="flex items-center gap-12 flex-1 min-w-0">
          <div
            className="w-40 h-40 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${getStrategyColor(debt?.payoffStrategy)}20` }}
          >
            <Icon
              name={getStrategyIcon(debt?.payoffStrategy)}
              size={20}
              color={getStrategyColor(debt?.payoffStrategy)}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-card-foreground truncate">
              {debt?.debtName}
            </h3>
            <p className="text-sm text-muted-foreground">
              {debt?.linkedAccount?.name || 'No linked account'}
            </p>
          </div>
        </div>
        <div className="flex gap-8 flex-shrink-0 ml-8">
          <button
            onClick={onEdit}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Edit debt"
          >
            <Icon name="Edit" size={18} color="var(--color-muted-foreground)" />
          </button>
          <button
            onClick={onDelete}
            className="p-8 hover:bg-destructive/10 rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Delete debt"
          >
            <Icon name="Trash2" size={18} color="var(--color-destructive)" />
          </button>
        </div>
      </div>

      <div className="space-y-12 mb-16">
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-muted-foreground">Current Balance</span>
          <span className="text-xl font-bold text-destructive">
            {formatCurrency(debt?.currentBalance)}
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-muted-foreground">Interest Rate</span>
          <span className="text-lg font-semibold text-foreground">
            {debt?.interestRate?.toFixed(2)}% APR
          </span>
        </div>
        <div className="flex justify-between items-baseline">
          <span className="text-sm text-muted-foreground">Minimum Payment</span>
          <span className="text-lg font-semibold text-foreground">
            {formatCurrency(debt?.minimumPayment)}
          </span>
        </div>
        {(debt?.additionalFees > 0 || debt?.insuranceFee > 0) && (
          <div className="flex justify-between items-baseline">
            <span className="text-sm text-muted-foreground">Additional Fees</span>
            <span className="text-sm font-medium text-warning">
              {formatCurrency(debt?.additionalFees + debt?.insuranceFee)}
            </span>
          </div>
        )}
        <div className="flex justify-between items-baseline pt-8 border-t border-border">
          <span className="text-sm font-medium text-muted-foreground">Total Monthly</span>
          <span className="text-lg font-bold text-foreground">
            {formatCurrency(totalMonthlyPayment)}
          </span>
        </div>
      </div>

      <div className="mb-16">
        <div className="flex justify-between items-center mb-8">
          <span className="text-sm text-muted-foreground">Progress</span>
          <span className="text-sm font-medium text-foreground">
            {progressPercentage?.toFixed(1)}% paid
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-8 overflow-hidden">
          <div
            className="h-full bg-success transition-all duration-300"
            style={{ width: `${Math.min(progressPercentage, 100)}%` }}
          ></div>
        </div>
      </div>

      {debt?.optimalPayment && (
        <div className="mb-16 p-12 bg-primary/10 rounded-lg border border-primary/20">
          <div className="flex items-center gap-8 mb-4">
            <Icon name="Sparkles" size={14} color="var(--color-primary)" />
            <span className="text-xs font-medium text-primary">AI Recommended Payment</span>
          </div>
          <p className="text-lg font-bold text-primary">
            {formatCurrency(debt?.optimalPayment)}/month
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-12 pt-16 border-t border-border">
        <div>
          <p className="text-xs text-muted-foreground mb-4">Monthly Interest</p>
          <p className="text-sm font-semibold text-destructive">
            {formatCurrency(monthlyInterest)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-4">Strategy</p>
          <p className="text-sm font-semibold text-foreground capitalize">
            {debt?.payoffStrategy}
          </p>
        </div>
      </div>

      <button
        onClick={onViewHistory}
        className="w-full mt-16 py-12 px-16 bg-muted hover:bg-muted/80 rounded-lg transition-smooth flex items-center justify-center gap-8"
      >
        <Icon name="History" size={16} color="var(--color-foreground)" />
        <span className="text-sm font-medium text-foreground">View Payment History</span>
      </button>

      {onViewPlans && (
        <button
          onClick={onViewPlans}
          className="w-full mt-12 py-12 px-16 bg-primary/10 hover:bg-primary/20 rounded-lg transition-smooth flex items-center justify-center gap-8 border border-primary/20"
        >
          <Icon name="FileText" size={16} color="var(--color-primary)" />
          <span className="text-sm font-medium text-primary">View Saved Plans</span>
        </button>
      )}
    </div>
  );
};

export default DebtCard;